import { BaseClientService } from "@/services/client/base-client.service";
import { getFirebaseIdToken } from "@/utils/firebase.utils";
import type { z } from "zod";

import type {
	BusinessWithRelations,
	SelectableBusiness,
} from "@flowpos-workspace/backend-database/types/business.types";
import type { SelectableUser } from "@flowpos-workspace/backend-database/types/user.types";

import type { billFormSchema } from "@/app/(private)/onboarding/@bill/_components/bill-form";
import type { businessFormSchema } from "@/app/(private)/onboarding/@business/_components/business-form";
import type { onboardingLocationFormSchema } from "@/app/(private)/onboarding/@location/_components/onboarding-location-form";

interface ApiError extends Error {
	status?: number;
}

interface ICreateUserParameters {
	fullName?: string;
	firebaseIdToken: string;
}

interface IUpsertOnboardingLocation {
	businessId: string;
	data: Omit<
		z.infer<typeof onboardingLocationFormSchema>,
		"needContact" | "place" | "dialCode"
	> & {
		placeId?: string;
	};
}

interface IPatchOnboardingBill {
	businessId: string;
	data: Omit<z.infer<typeof billFormSchema>, "needCustomFelConfig">;
}

export enum UserQueryKeys {
	FetchMe = "FETCH_ME",
	FetchUserOnboarding = "FETCH_USER_ONBOARDING",
}

export enum UserMutationKeys {
	CreateUser = "CREATE_USER",
	UpsertOnboardingBusiness = "UPSERT_ONBOARDING_BUSINESS",
	UpsertOnboardingLocation = "UPSERT_ONBOARDING_LOCATION",
	PatchOnboardingBill = "PATCH_ONBOARDING_BILL",
}

export const useUserService = () => {
	const path = "/users";

	const constructPath = (subPath: string) => `${path}/${subPath}`;

	const fetchMe = async () => {
		const token = await getFirebaseIdToken();

		return BaseClientService.instance().request<SelectableUser>({
			path: constructPath("me"),
			init: {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		});
	};

	const fetchUserOnboarding = async () => {
		const token = await getFirebaseIdToken();

		return BaseClientService.instance().request<BusinessWithRelations>({
			path: constructPath("me/onboarding"),
			init: {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		});
	};

	const upsertOnboardingBusiness = async (
		data: z.infer<typeof businessFormSchema>,
	) => {
		const token = await getFirebaseIdToken();
		const path = constructPath("me/onboarding/business");

		console.log("[upsertOnboardingBusiness] Starting request with data:", data);

		const makeRequest = async (method: string) => {
			console.log(
				`[upsertOnboardingBusiness] Attempting ${method} request to ${path}`,
			);
			try {
				const response =
					await BaseClientService.instance().request<SelectableBusiness>({
						path,
						init: {
							body: data,
							method,
							headers: {
								Authorization: `Bearer ${token}`,
							},
						},
					});
				console.log(
					`[upsertOnboardingBusiness] ${method} request successful:`,
					response,
				);
				return response;
			} catch (error) {
				console.error(
					`[upsertOnboardingBusiness] ${method} request failed:`,
					error,
				);
				throw error;
			}
		};

		try {
			return await makeRequest("POST");
		} catch (error: unknown) {
			const apiError = error as ApiError;
			if (apiError.status === 405) {
				console.log(
					"[upsertOnboardingBusiness] Received 405 error, attempting PUT request",
				);
				return await makeRequest("PUT");
			}
			throw error;
		}
	};

	const upsertOnboardingLocation = async ({
		businessId,
		data,
	}: IUpsertOnboardingLocation) => {
		const token = await getFirebaseIdToken();

		return BaseClientService.instance().request<SelectableBusiness>({
			path: constructPath(`me/onboarding/${businessId}/location`),
			init: {
				body: data,
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		});
	};

	const patchOnboardingBill = async ({
		businessId,
		data,
	}: IPatchOnboardingBill) => {
		const token = await getFirebaseIdToken();

		return BaseClientService.instance().request<SelectableBusiness>({
			path: constructPath(`me/onboarding/${businessId}/bill`),
			init: {
				body: data,
				method: "PATCH",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		});
	};

	const createUser = ({ firebaseIdToken, ...rest }: ICreateUserParameters) =>
		BaseClientService.instance().request<SelectableUser>({
			path,
			init: {
				method: "POST",
				body: rest,
				headers: {
					Authorization: `Bearer ${firebaseIdToken}`,
				},
			},
		});

	return {
		createUser,
		fetchMe,
		fetchUserOnboarding,
		upsertOnboardingBusiness,
		upsertOnboardingLocation,
		patchOnboardingBill,
	};
};
